import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { fetchBacklogConfig, fetchBacklogConnection } from '@/services/backlog'
import { useStore } from '@/store/useStore'

/**
 * Whether to point the reader at Settings to connect their Backlog account.
 *
 * Two conditions, and both have to be read from the server. The space may not
 * use Backlog at all, in which case a nudge is noise nobody can act on; and the
 * reader may already have a working grant, which only a fresh read can tell —
 * an application revoked in Backlog leaves the stored row untouched.
 *
 * Read again whenever the route changes or the window regains focus. Connecting
 * happens on another page and comes back here, and the sidebar stays mounted
 * the whole time — a single read on first mount would leave the Settings dot
 * up after the account is already connected.
 *
 * Silent on failure, and `fetchBacklogConnection` does throw. This only
 * decides whether to show an invitation, and a Backlog that cannot be reached
 * is not a reason to put an error in front of somebody who may not use it.
 *
 * @returns `true` only when the space has Backlog configured and the reader has
 *   no working connection to it.
 */
export function useBacklogConnectNudge(): boolean {
  const { pathname } = useLocation()
  const [needsConnection, setNeedsConnection] = useState(false)

  useEffect(() => {
    let cancelled = false
    let latest = 0

    function check() {
      const request = ++latest
      void Promise.all([fetchBacklogConfig(), fetchBacklogConnection()])
        .then(([config, connection]) => {
          if (cancelled || request !== latest) return
          // A working connection ends the one-time notice for good, including
          // after a later disconnect. The Settings dot uses the same answer, so
          // it leaves as soon as this read says the account is connected.
          if (connection.status === 'ACTIVE') {
            useStore.getState().dismissBacklogNudge()
            setNeedsConnection(false)
            return
          }
          setNeedsConnection(Boolean(config))
        })
        .catch(() => {
          if (!cancelled && request === latest) setNeedsConnection(false)
        })
    }

    check()
    window.addEventListener('focus', check)
    return () => {
      cancelled = true
      window.removeEventListener('focus', check)
    }
  }, [pathname])

  return needsConnection
}
